import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Bank as Landmark, Globe, Mountains as Mountain, BookOpenText as BookOpen, BookBookmark as BookMarked, Waves } from "@phosphor-icons/react";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { SeasonLockedScreen } from "../components/game/SeasonLockedScreen";
import { RelayLoader } from "../components/RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;
const COLOR = { ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)", line: "var(--pr-line)" };

const domain = (url) => url.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];

const SECTIONS = [
  {
    id: "money-management", heading: "Money management", sub: "Give every dollar a home the moment it lands.",
    items: [
      { id: "relay-financial", icon: Landmark, kind: "Banking", name: "Relay Financial", url: "https://relayfi.com",
        blurb: "Bucket banking built for small business. Taxes, paid time off, savings and take-home each get their own account, so every payout has somewhere to land." },
      { id: "wise", icon: Globe, kind: "Banking", name: "Wise", url: "https://wi.se/paydayrelay",
        blurb: "For earning or spending across borders. Keeps multiple currencies straight without the usual bank fees, a staple for expat and traveling 1099 earners." },
    ],
  },
  {
    id: "productivity", heading: "Productivity", sub: "Protect the hours that clear the relay.",
    items: [
      { id: "caveday", icon: Mountain, kind: "Focus", name: "Cave Day", url: "https://www.caveday.org/try?via=dove",
        blurb: "Facilitated deep-work sessions with a real host and a clear start and finish. Useful when a relay needs a few hours of undivided attention." },
    ],
  },
  {
    id: "money-systems", heading: "Money systems", sub: "The thinking the relay is built on.",
    items: [
      { id: "your-money", icon: BookOpen, kind: "Book", name: "Your Money or Your Life", url: "https://yourmoneyoryourlife.com/",
        blurb: "Vicki Robin's classic on trading life energy for money, the shift that makes a weekly target feel like freedom rather than pressure." },
      { id: "profit-first", icon: BookMarked, kind: "Book", name: "Profit First", url: "https://mikemichalowicz.com/profit-first/",
        blurb: "Mike Michalowicz's allocation-first money system, and the reason every dollar in Payday Relay gets a job before it gets spent." },
    ],
  },
  {
    id: "business-inspiration", heading: "Business inspiration", sub: "On building work worth doing.",
    items: [
      { id: "let-my-people-go-surfing", icon: Waves, kind: "Book", name: "Let My People Go Surfing", url: "https://www.patagonia.com/product/let-my-people-go-surfing-revised-paperback-book/BK067.html",
        blurb: "Yvon Chouinard on building Patagonia, a company run on values, responsibility, and a healthier relationship with work." },
    ],
  },
];

const ResourceCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-2xl p-5 pr-card h-full transition-transform hover:-translate-y-0.5"
      data-testid={`stack-link-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "var(--pr-bg-alt)", border: `1px solid ${COLOR.line}` }}
        >
          <Icon className="w-[18px] h-[18px]" style={{ color: COLOR.ink }} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: COLOR.muted }}>{item.kind}</p>
          <p className="text-sm font-bold leading-snug" style={{ color: COLOR.ink }}>{item.name}</p>
        </div>
        <ArrowUpRight className="w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: COLOR.muted }} />
      </div>
      <p className="text-xs leading-relaxed mt-3" style={{ color: COLOR.ink2 }}>{item.blurb}</p>
      <p className="text-[11px] mt-3 pt-3 border-t" style={{ color: COLOR.muted, borderColor: COLOR.line }}>{domain(item.url)}</p>
    </a>
  );
};

export default function ResourceLibraryPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("loading"); // loading | sprint_gate | gate | ready | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/resources`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/resources", { replace: true }); return; }
        if (res.status === 403) {
          const body = await res.json().catch(() => null);
          setView(body?.detail?.code === "season_pass_required" ? "sprint_gate" : "gate");
          return;
        }
        setInfo(await res.json());
        setView("ready");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (view === "error") return <CaddyErrorCard />;
  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }}>
        <RelayLoader label="Opening the library…" />
      </div>
    );
  }
  if (view === "sprint_gate") {
    return (
      <SeasonLockedScreen
        id="resources-sprint-gate"
        icon="📚"
        title="The Resource Library"
        body="The banking, focus, and reading picks behind the Payday Relay method, the stack Shari actually uses to run her own 52-week race."
        bullets={["Bucket banking that splits every payout automatically", "Deep-work sprints for the weeks that need real focus", "The two books the whole system is built on"]}
      />
    );
  }
  if (view === "gate") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="resources-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            The Resource Library rides with your Season Pass
          </h1>
          <Link to="/start/free-sprint" className="inline-block px-8 py-3 rounded-full font-bold pr-btn-primary" data-testid="resources-gate-cta">
            Start Free Sprint →
          </Link>
        </div>
      </div>
    );
  }

  const total = SECTIONS.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="resources-page">
      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="resources" sheetUrl={info?.sheet_url} />
        <main className="px-5 py-6 lg:px-0 lg:pt-0 pb-16">
          <div className="max-w-3xl mx-auto lg:mx-0">
            <Link to="/app" className="lg:hidden inline-flex items-center gap-1.5 text-sm font-semibold mb-4" style={{ color: COLOR.ink2 }} data-testid="resources-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Payday Relay
            </Link>

            {/* Help Guides first: the answers, before the recommendations */}
            <Link
              to="/learn"
              className="flex items-center justify-between gap-4 rounded-2xl p-4 mb-6 pr-card transition-transform hover:-translate-y-0.5"
              data-testid="resources-help-guides-link"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 flex-shrink-0" style={{ color: COLOR.ink }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: COLOR.ink }}>Help Guides</p>
                  <p className="text-xs leading-relaxed" style={{ color: COLOR.ink2 }}>
                    How the system works, income and payouts, taxes and set-asides, pacing your work.
                  </p>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 flex-shrink-0" style={{ color: COLOR.muted }} />
            </Link>

            {/* Library masthead, a curated shelf, not a link dump */}
            <header className="pb-6 mb-8 border-b" style={{ borderColor: COLOR.line }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: COLOR.muted }}>
                {total} picks · selected by Shari
              </p>
              <h1 className="text-3xl lg:text-4xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Resource Library</h1>
              <p className="text-sm leading-relaxed max-w-xl" style={{ color: COLOR.ink2 }} data-testid="resources-intro">
                The tools and books behind the Payday Relay method, what Shari uses herself as a 1099 remote
                worker and expat to run her own race. None of it is required to run your relays.
              </p>
              <nav className="flex flex-wrap gap-2 mt-5" aria-label="Library sections">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
                    style={{ border: `1px solid ${COLOR.line}`, color: COLOR.ink2 }}
                    data-testid={`resources-nav-${s.id}`}
                  >
                    {s.heading}
                  </a>
                ))}
              </nav>
            </header>

            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} className="mb-10 scroll-mt-8" data-testid={`resources-section-${s.id}`}>
                <div className="flex items-baseline gap-2 mb-0.5">
                  <h2 className="text-lg font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>{s.heading}</h2>
                  <span className="text-[11px]" style={{ color: COLOR.muted }}>{s.items.length}</span>
                </div>
                <p className="text-xs mb-4" style={{ color: COLOR.muted }}>{s.sub}</p>
                <div className="grid sm:grid-cols-2 gap-4 items-stretch">
                  {s.items.map((t) => <ResourceCard key={t.id} item={t} />)}
                </div>
              </section>
            ))}

            <p className="text-[11px]" style={{ color: COLOR.muted }} data-testid="stack-disclosure">
              Some links are referrals, using them supports Payday Relay at no extra cost to you.
            </p>
            <p className="text-[11px] text-center leading-relaxed px-4 py-8" style={{ color: COLOR.muted }}>
              Payday Relay is an educational tool, not financial, tax, or investment advice.{" "}
              <Link to="/terms" className="underline">Terms</Link>
            </p>
          </div>
        </main>
      </div>
      <div className="h-20 lg:hidden" />
      <CaddyBottomNav active="resources" />
    </div>
  );
}
